import { Badge } from '@sgme/ui';
import classNames from 'classnames';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useHelpCenterFactsheetLink } from 'hooks/use-help-center-factsheet-link';
import { useAppSelector } from 'hooks/useAppSelector';
import { AccordionItemProps } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import { FormattedMessage } from 'react-intl';
import { SpAccordionHeader } from 'routes/essential-workspace/components/quote-panel/collapse/accordion-header';
import { FactsheetDocItem } from 'routes/essential-workspace/components/quote-panel/collapse/docs-item/factsheet';
import { EssentialInfoDocItem } from 'routes/essential-workspace/components/quote-panel/collapse/docs-item/info-doc';
import {
  essentialFormInfoProductSubtype,
  essentialFormInfoProductType,
  selectEssentialFormInfosDocuments,
} from 'state/essential/infos/selectors';
import { selectDocumentErrorCountWhenReady } from 'state/selectors/common/document';

import { CollapseKey } from '../types';

type DocsCollapseItemProps = Omit<AccordionItemProps, 'eventKey'> & { eventKey: CollapseKey; canShowError?: boolean };

export function DocsAccordionItem({ className, eventKey, canShowError = true, ...props }: DocsCollapseItemProps) {
  const tabId = useAssertedActiveTabId();
  const infoFormDocs = useAppSelector(state => selectEssentialFormInfosDocuments(state, tabId));
  const productType = useAppSelector(state => essentialFormInfoProductType(state, tabId));
  const productSubType = useAppSelector(state => essentialFormInfoProductSubtype(state, tabId));
  const factsheetLink = useHelpCenterFactsheetLink(productType, productSubType);
  const errorCount = useAppSelector(state => selectDocumentErrorCountWhenReady(state, tabId));
  const hasErrors = errorCount > 0;

  const hasDocs = (infoFormDocs.length ?? 0) > 0 || !!factsheetLink;

  if (!hasDocs) {
    return null;
  }

  return (
    <Accordion.Item
      {...props}
      eventKey={eventKey}
      className={classNames(
        'bg-secondary bg-opacity-10',
        { 'border-danger border': hasErrors },
        { 'border-0': !hasErrors },
        className,
      )}
    >
      <SpAccordionHeader>
        <div className="p-4 text-start text-secondary h5 lh-1 mb-0 w-100 flex-x gap-3">
          <FormattedMessage id="documentation.title" defaultMessage="Documentation" />
          {hasErrors && (
            <Badge purpose="discreet" color="danger" isPill>
              {errorCount}
            </Badge>
          )}
        </div>
      </SpAccordionHeader>

      <Accordion.Collapse eventKey={eventKey} className="p-4 pt-0">
        <ul className="list-group list-group-flush" aria-label="Quote documentation">
          {infoFormDocs.map(doc => (
            <EssentialInfoDocItem key={doc.type} doc={doc} canShowError={canShowError} />
          ))}
          {!!factsheetLink && <FactsheetDocItem href={factsheetLink} />}
        </ul>
      </Accordion.Collapse>
    </Accordion.Item>
  );
}
